import { useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type FieldPath, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { startOfToday, startOfTomorrow } from "date-fns";
import { CalendarClock, Gavel, Plus, Truck, Warehouse, Wheat } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { displayErrorMessage, getCountryObjectFromCache } from "../../utils";
import {
  addBidRequest,
  addBidRequestReset,
  listCommodities,
  listCommodityQualityGrades,
  listCommodityQualityGradesReset,
  listExchangeLocations,
  listUnitsOfMeasure,
  listTraders,
} from "../../actions";
import { orderByLocalName } from "../../utils/i18n";
import { CommodityLineRow } from "./components/CommodityLineRow";
import { toBidRequestPayload } from "./adapter";
import { createBidRequestSchema } from "./schema";
import type {
  BidRequestFormValues,
  CommodityOption,
  EligibleAggregator,
  ExchangeLocationOption,
  QualityGradeOption,
  UnitOfMeasureOption,
} from "./types";

const LIST_PATH = "/representative/bidRequests";

const EMPTY_LINE = { commodity: "", quantity: "", unit_of_measure: "", quality: "" };

const toErrorMessage = (error: unknown): string => {
  if (!error) {
    return "";
  }
  if (typeof error === "string") {
    return error;
  }
  if (Array.isArray(error)) {
    return error.map(toErrorMessage).filter(Boolean).join(", ");
  }
  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }
  return "";
};

const MAPPED_ERROR_FIELDS: FieldPath<BidRequestFormValues>[] = [
  "delivery_date",
  "delivery_location",
  "posting_start_date",
  "posting_end_date",
];

interface SchoolOrganization {
  id?: number;
  name?: string;
  eligible_aggregators?: EligibleAggregator[];
}

interface StateProps {
  commodities: CommodityOption[];
  units: UnitOfMeasureOption[];
  qualityGrades: QualityGradeOption[];
  school?: SchoolOrganization;
  exchangeLocations: ExchangeLocationOption[];
  isSaving: boolean;
  addError: Record<string, unknown> | undefined;
}

interface DispatchProps {
  listCommodities: () => void;
  listUnitsOfMeasure: () => void;
  listCommodityQualityGrades: () => void;
  listCommodityQualityGradesReset: () => void;
  listTraders: () => void;
  listExchangeLocations: () => void;
  addBidRequest: (payload: ReturnType<typeof toBidRequestPayload>) => Promise<unknown>;
  addBidRequestReset: () => void;
}

type BidRequestCreateProps = StateProps & DispatchProps & RouteComponentProps;

export const BidRequestCreate = ({
  commodities,
  units,
  qualityGrades,
  school,
  exchangeLocations,
  isSaving,
  addError,
  listCommodities,
  listUnitsOfMeasure,
  listCommodityQualityGrades,
  listCommodityQualityGradesReset,
  listTraders,
  listExchangeLocations,
  addBidRequest,
  addBidRequestReset,
  history,
}: BidRequestCreateProps) => {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      createBidRequestSchema({
        required: t("bidRequests.form.errors.required"),
        quantityPositive: t("bidRequests.form.errors.quantityPositive"),
        itemsRequired: t("bidRequests.form.errors.itemsRequired"),
        endAfterStart: t("bidRequests.form.errors.endAfterStart"),
        endNotBeforeTomorrow: t("bidRequests.form.errors.endNotBeforeTomorrow"),
        deliveryAfterPostingEnd: t("bidRequests.form.errors.deliveryAfterPostingEnd"),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<BidRequestFormValues>({
    defaultValues: {
      items: [EMPTY_LINE],
      delivery_date: null,
      delivery_location: "",
      posting_start_date: startOfToday(),
      posting_end_date: null,
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });

  useEffect(() => {
    listCommodities();
    listUnitsOfMeasure();
    listCommodityQualityGrades();
    listTraders();
    listExchangeLocations();
    return () => {
      addBidRequestReset();
      listCommodityQualityGradesReset();
    };
  }, [
    listCommodities,
    listUnitsOfMeasure,
    listCommodityQualityGrades,
    listCommodityQualityGradesReset,
    listTraders,
    listExchangeLocations,
    addBidRequestReset,
  ]);

  const qualityOptions = useMemo(
    () => qualityGrades.map(grade => ({ value: grade.code, label: grade.name })),
    [qualityGrades]
  );

  const deliveryLocationValue = useWatch({ control, name: "delivery_location" });

  useEffect(() => {
    if (school?.name && !deliveryLocationValue) {
      setValue("delivery_location", school.name, { shouldValidate: false });
    }
  }, [school?.name, deliveryLocationValue, setValue]);

  const eligibleAggregators = useMemo(
    () => school?.eligible_aggregators ?? [],
    [school?.eligible_aggregators]
  );

  const deliveryLocationOptions = useMemo(() => {
    const schoolLocations: string[] = [];
    if (school?.name) {
      schoolLocations.push(school.name);
    }
    exchangeLocations
      .filter(location =>
        location.assignments?.some(assignment => assignment.trader_organization === school?.id)
      )
      .forEach(location => {
        if (!schoolLocations.includes(location.name)) {
          schoolLocations.push(location.name);
        }
      });
    const aggregatorAreas = Array.from(
      new Set(
        eligibleAggregators
          .map(aggregator => aggregator.location_name)
          .filter((name): name is string => !!name && !schoolLocations.includes(name))
      )
    ).sort((a, b) => a.localeCompare(b));
    return { schoolLocations, aggregatorAreas };
  }, [school?.id, school?.name, exchangeLocations, eligibleAggregators]);

  useEffect(() => {
    clearErrors();
    if (!addError || Object.keys(addError).length === 0) {
      return;
    }
    Object.entries(addError).forEach(([fieldName, message]) => {
      if (MAPPED_ERROR_FIELDS.includes(fieldName as FieldPath<BidRequestFormValues>)) {
        setError(fieldName as FieldPath<BidRequestFormValues>, {
          type: "server",
          message: toErrorMessage(message),
        });
      }
    });
  }, [addError, clearErrors, setError]);

  const generalError = useMemo(() => {
    if (!addError) {
      return "";
    }
    const messages = Object.entries(addError)
      .filter(
        ([fieldName]) => !MAPPED_ERROR_FIELDS.includes(fieldName as FieldPath<BidRequestFormValues>)
      )
      .map(([, message]) => toErrorMessage(message))
      .filter(Boolean);
    return messages.join(" ");
  }, [addError]);

  const onSubmit = async (values: BidRequestFormValues) => {
    const country = getCountryObjectFromCache() as { tz_name?: string };
    const result = await addBidRequest(toBidRequestPayload(values, country?.tz_name));
    if (result) {
      history.push(LIST_PATH);
    }
  };

  return (
    <main className="flex w-full flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 font-semibold text-2xl">
          <Gavel className="size-6" />
          {t("bidRequests.form.title")}
        </h1>
        <p className="text-muted-foreground text-sm">{t("bidRequests.form.titleHint")}</p>
      </div>

      {generalError && displayErrorMessage(generalError)}

      <form noValidate className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="size-5" />
              {t("bidRequests.form.postingSection")}
            </CardTitle>
            <CardDescription>{t("bidRequests.form.postingHint")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-start gap-4">
            <Controller
              control={control}
              name="posting_start_date"
              render={({ field, fieldState }) => (
                <Field className="min-w-56 flex-1">
                  <FieldLabel htmlFor="posting_start_date">
                    {t("bidRequests.form.postingStartDate")}
                  </FieldLabel>
                  <DatePicker
                    id="posting_start_date"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("bidRequests.form.selectDate")}
                    disabled={{ before: startOfToday() }}
                    invalid={!!fieldState.error}
                    isDisabled={isSaving}
                  />
                  <FieldError>{fieldState.error?.message}</FieldError>
                </Field>
              )}
            />

            <Controller
              control={control}
              name="posting_end_date"
              render={({ field, fieldState }) => (
                <Field className="min-w-56 flex-1">
                  <FieldLabel htmlFor="posting_end_date">
                    {t("bidRequests.form.postingEndDate")}
                  </FieldLabel>
                  <DatePicker
                    id="posting_end_date"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("bidRequests.form.selectDate")}
                    disabled={{ before: startOfTomorrow() }}
                    invalid={!!fieldState.error}
                    isDisabled={isSaving}
                  />
                  <FieldError>{fieldState.error?.message}</FieldError>
                </Field>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wheat className="size-5" />
              {t("bidRequests.form.commoditiesSection")}
            </CardTitle>
            <CardDescription>{t("bidRequests.form.commoditiesHint")}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {fields.map((line, index) => (
              <CommodityLineRow
                key={line.id}
                index={index}
                control={control}
                setValue={setValue}
                commodities={commodities}
                units={units}
                qualityOptions={qualityOptions}
                canRemove={fields.length > 1}
                onRemove={() => remove(index)}
                isDisabled={isSaving}
              />
            ))}
            {typeof errors.items?.message === "string" && (
              <p role="alert" className="text-destructive text-sm">
                {errors.items.message}
              </p>
            )}
            <div>
              <WFPButton
                type="button"
                variant="secondary"
                disabled={isSaving}
                onClick={() => append({ ...EMPTY_LINE })}
              >
                <Plus className="size-4" />
                {t("bidRequests.form.addCommodity")}
              </WFPButton>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="size-5" />
              {t("bidRequests.form.deliverySection")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-start gap-4">
            <Controller
              control={control}
              name="delivery_date"
              render={({ field, fieldState }) => (
                <Field className="min-w-56 flex-1">
                  <FieldLabel htmlFor="delivery_date">
                    {t("bidRequests.form.deliveryDate")}
                  </FieldLabel>
                  <DateTimePicker
                    id="delivery_date"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("bidRequests.form.selectDate")}
                    timeAriaLabel={t("bidRequests.form.deliveryTime")}
                    minHour={4}
                    maxHour={20}
                    invalid={!!fieldState.error}
                  />
                  <FieldError>{fieldState.error?.message}</FieldError>
                </Field>
              )}
            />

            <Controller
              control={control}
              name="delivery_location"
              render={({ field, fieldState }) => (
                <Field className="min-w-56 flex-1">
                  <FieldLabel htmlFor="delivery_location">
                    {t("bidRequests.form.deliveryLocation")}
                  </FieldLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={isSaving}>
                    <SelectTrigger
                      id="delivery_location"
                      aria-invalid={!!fieldState.error}
                      onBlur={field.onBlur}
                      className="w-full border-border"
                    >
                      <SelectValue
                        placeholder={t("bidRequests.form.deliveryLocationPlaceholder")}
                      />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-72">
                      <SelectGroup>
                        <SelectLabel>{t("bidRequests.form.schoolLocationsGroup")}</SelectLabel>
                        {deliveryLocationOptions.schoolLocations.map(name => (
                          <SelectItem key={name} value={name}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      {deliveryLocationOptions.aggregatorAreas.length > 0 && (
                        <>
                          <SelectSeparator />
                          <SelectGroup>
                            <SelectLabel>{t("bidRequests.form.aggregatorAreasGroup")}</SelectLabel>
                            {deliveryLocationOptions.aggregatorAreas.map(name => (
                              <SelectItem key={name} value={name}>
                                {name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <FieldError>{fieldState.error?.message}</FieldError>
                </Field>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Warehouse className="size-5" />
              {t("bidRequests.form.aggregatorsSection")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-muted-foreground text-sm">{t("bidRequests.form.aggregatorsHint")}</p>
            {eligibleAggregators.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {eligibleAggregators.map(aggregator => (
                  <li key={aggregator.id} className="font-medium text-sm">
                    {aggregator.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">
                {t("bidRequests.form.aggregatorsEmpty")}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <WFPButton
            type="button"
            variant="outline"
            disabled={isSaving}
            onClick={() => history.push(LIST_PATH)}
          >
            {t("common.cancel")}
          </WFPButton>
          <WFPButton type="submit" disabled={isSaving}>
            {t("bidRequests.form.postRequest")}
          </WFPButton>
        </div>
      </form>
    </main>
  );
};

interface RequestState<T> {
  isFetching?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  error?: Record<string, unknown>;
  data: T;
}

interface RootState {
  listCommodities: RequestState<{ results?: CommodityOption[] }>;
  listUnitsOfMeasure: RequestState<{ results?: UnitOfMeasureOption[] }>;
  listCommodityQualityGrades: RequestState<{ results?: QualityGradeOption[] }>;
  listTraders: RequestState<{ results?: SchoolOrganization[] }>;
  listExchangeLocations: RequestState<{ results?: ExchangeLocationOption[] }>;
  addBidRequest: RequestState<unknown>;
}

export const mapStateToProps = (state: RootState): StateProps => ({
  commodities: orderByLocalName(state.listCommodities.data.results) as CommodityOption[],
  units: state.listUnitsOfMeasure.data.results ?? [],
  qualityGrades: state.listCommodityQualityGrades.data.results ?? [],
  school: state.listTraders.data.results?.[0],
  exchangeLocations: state.listExchangeLocations.data.results ?? [],
  isSaving: !!state.addBidRequest.isLoading,
  addError:
    state.addBidRequest.error && Object.keys(state.addBidRequest.error).length > 0
      ? state.addBidRequest.error
      : undefined,
});

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown): Promise<unknown> =>
  dispatch(action as UnknownAction) as unknown as Promise<unknown>;

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listCommodities: () => dispatchAction(dispatch, listCommodities({ page: 1, page_size: 9999 })),
  listUnitsOfMeasure: () => dispatchAction(dispatch, listUnitsOfMeasure()),
  listCommodityQualityGrades: () => dispatchAction(dispatch, listCommodityQualityGrades()),
  listCommodityQualityGradesReset: () =>
    dispatchAction(dispatch, listCommodityQualityGradesReset()),
  listTraders: () => dispatchAction(dispatch, listTraders()),
  listExchangeLocations: () => dispatchAction(dispatch, listExchangeLocations()),
  addBidRequest: payload => dispatchAction(dispatch, addBidRequest(payload)),
  addBidRequestReset: () => dispatchAction(dispatch, addBidRequestReset()),
});

export const ConnectedBidRequestCreate = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(BidRequestCreate));
